import React, { useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { getHotels } from "../../services/api";
import {
  DatePreset,
  ReportFilters,
  getRangeByPreset,
} from "../../pages/admin/reports/filterUtils";
import { getTokenInfo } from "../../utils/auth";

interface ReportsFiltersProps {
  value: ReportFilters;
  onChange: (next: ReportFilters) => void;
  showOrderFilters?: boolean;
}

const PRESET_OPTIONS: Array<{ label: string; value: DatePreset }> = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "this_week" },
  { label: "This Month", value: "this_month" },
  { label: "Custom", value: "custom" },
];

interface Hotel {
  _id: string;
  hotelName: string;
}

const ReportsFilters: React.FC<ReportsFiltersProps> = ({
  value,
  onChange,
  showOrderFilters = true,
}) => {
  const isOnlineSource = value.orderSource === "online";
  const isKioskSource = value.orderSource === "kiosk";
  const isCounterSource = value.orderSource === "counter";
  const shouldShowDeliveryPickup = !isKioskSource && !isCounterSource;
  const shouldShowDineinTakeaway = !isOnlineSource && !isCounterSource;
  const tokenInfo = getTokenInfo();
  const isMasterAdmin = tokenInfo?.role === "master_admin";
  const [hotels, setHotels] = React.useState<Hotel[]>([]);
  const [locationDropdownOpen, setLocationDropdownOpen] = React.useState(false);

  const isOrderTypeAllowedForSource = (
    orderSource: ReportFilters["orderSource"],
    orderType: ReportFilters["orderType"],
  ) => {
    if (orderType === "all") return true;
    if (orderSource === "online") {
      return orderType !== "dine_in" && orderType !== "takeaway";
    }
    if (orderSource === "kiosk") {
      return orderType !== "delivery" && orderType !== "pickup";
    }
    if (orderSource === "counter") {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (isCounterSource && value.orderType !== "all") {
      onChange({ ...value, orderType: "all" });
    }
  }, [isCounterSource, onChange, value]);

  useEffect(() => {
    if (!isMasterAdmin) return;

    const fetchHotels = async () => {
      try {
        const res = await getHotels();
        setHotels(res.data?.data ?? []);
      } catch (error) {
        console.error("Failed to fetch hotels", error);
      }
    };

    fetchHotels();
  }, [isMasterAdmin]);

  const handlePresetChange = (preset: DatePreset) => {
    if (preset === "custom") {
      onChange({ ...value, datePreset: preset });
      return;
    }

    const range = getRangeByPreset(preset);
    onChange({
      ...value,
      datePreset: preset,
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };

  const handleOrderSourceChange = (nextOrderSource: ReportFilters["orderSource"]) => {
    onChange({
      ...value,
      orderSource: nextOrderSource,
      orderType:
        nextOrderSource === "counter" ||
        !isOrderTypeAllowedForSource(nextOrderSource, value.orderType)
          ? "all"
          : value.orderType,
    });
  };

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
      <h2 className="mb-4 text-sm font-semibold text-gray-700">Filters</h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div>
          <p className="mb-2 text-xs font-semibold text-gray-500">Date Range</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
            {PRESET_OPTIONS.map((option) => {
              const isActive = value.datePreset === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handlePresetChange(option.value)}
                  className={`rounded-md border px-3 py-2 text-xs font-medium transition ${
                    isActive
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          {value.datePreset === "custom" && (
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input
                type="date"
                value={value.startDate}
                onChange={(e) => onChange({ ...value, startDate: e.target.value })}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="date"
                value={value.endDate}
                onChange={(e) => onChange({ ...value, endDate: e.target.value })}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>

        {isMasterAdmin && (
          <div className="relative">
            <label className="mb-2 block text-xs font-semibold text-gray-500">
              Location
            </label>
            <button
              type="button"
              onClick={() => setLocationDropdownOpen((prev) => !prev)}
              className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span>
                {value.restaurantId === "all"
                  ? "All Locations"
                  : hotels.find((h) => h._id === value.restaurantId)?.hotelName ||
                    "Select Location"}
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${
                  locationDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {locationDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setLocationDropdownOpen(false)}
                />
                <div className="absolute left-0 z-20 mt-2 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                  <div className="max-h-64 overflow-y-auto py-1">
                    <button
                      type="button"
                      onClick={() => {
                        onChange({ ...value, restaurantId: "all" });
                        setLocationDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                        value.restaurantId === "all"
                          ? "bg-blue-50 font-semibold text-blue-600"
                          : "text-gray-700"
                      }`}
                    >
                      All Locations
                    </button>
                    {hotels.map((hotel) => (
                      <button
                        key={hotel._id}
                        type="button"
                        onClick={() => {
                          onChange({ ...value, restaurantId: hotel._id });
                          setLocationDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                          value.restaurantId === hotel._id
                            ? "bg-blue-50 font-semibold text-blue-600"
                            : "text-gray-700"
                        }`}
                      >
                        {hotel.hotelName}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {showOrderFilters && (
          <div>
            <label className="mb-2 block text-xs font-semibold text-gray-500">
              Order Source
            </label>
            <select
              value={value.orderSource}
              onChange={(e) =>
                handleOrderSourceChange(e.target.value as ReportFilters["orderSource"])
              }
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Sources</option>
              <option value="online">Online</option>
              <option value="kiosk">Kiosk</option>
              <option value="counter">Counter</option>
            </select>
          </div>
        )}

        {showOrderFilters && (
          <div>
            <label className="mb-2 block text-xs font-semibold text-gray-500">
              Order Type
            </label>
            <select
              value={value.orderType}
              onChange={(e) =>
                onChange({
                  ...value,
                  orderType: e.target.value as ReportFilters["orderType"],
                })
              }
              disabled={isCounterSource}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
            >
              <option value="all">All Types</option>
              {shouldShowDeliveryPickup && <option value="delivery">Delivery</option>}
              {shouldShowDeliveryPickup && <option value="pickup">Pickup</option>}
              {shouldShowDineinTakeaway && <option value="dine_in">Dine-In</option>}
              {shouldShowDineinTakeaway && <option value="takeaway">Takeaway</option>}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsFilters;
